"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";

import type { StaticImageData } from "next/image";
import type { Media } from "@mw/shared/payload-types";
import type { SoundEffectExploreItem } from "@ui/components/audio/CategoryExploreList";
import type { SearchSuggestion } from "@ui/components/core/SearchInput";
import type { SuggestionPill } from "@ui/components/audio/AudioGenerationWidget";
import type { ILink } from "@components/core/Link";
import type { FormattedCTA } from "@utilities/payload";
import type { CategoryGridItem } from "@ui/components/core/CategoryGrid/CategoryGrid";

import { AudioPlayerProvider, useAudioPlayer } from "@ui/components/audio/AudioPlayerContext";
import { HeroSoundEffectsCategoryExplore } from "@ui/components/heroes/HeroSoundEffectsCategoryExplore";
import { MusicExploreList } from "@ui/components/music/MusicExploreList";
import { MusicGenerationWidget } from "@ui/components/music/MusicGenerationWidget";
import PageSection from "@ui/components/composers/PageSection";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { AudioListProvider } from "@/app/_providers/audio-list";
import clsx from "clsx";

import { MusicSearchSection } from "./MusicSearchSection";
import { MusicStickyBottomWidget } from "./MusicStickyBottomWidget";
import { MusicSignUpPopoverSection } from "./MusicSignUpPopoverSection";
import { MusicSimilarCollections } from "./MusicSimilarCollections";
import { useMusicDownload } from "./hooks/useMusicDownload";
import { useMusicGeneration } from "./hooks/useMusicGeneration";

interface CategorySearchResult {
  id: string;
  slug: string;
  title: string;
  breadcrumbName?: string | null;
}

interface MusicCategoryPageClientV2Props {
  breadcrumbs: { href?: string; title: string }[];
  title: string;
  description?: string | null;
  heroCover?: Media | StaticImageData | null;
  categoryName: string;
  soundItems: SoundEffectExploreItem[];
  searchSuggestions: SearchSuggestion[];
  generateCtaHref: string;
  onCategorySelect?: (slug: string) => void;
  onCategorySearch?: (query: string, signal: AbortSignal) => Promise<{ results: CategorySearchResult[] }>;
  onSearchSubmit?: (query: string) => void;
  generateFooterTitle: string;
  generateFooterSubtitle?: string;
  signUpPopoverTitle: string;
  signUpPopoverCta: string;
  signUpPopoverFeatures: [string, ...string[]];
  signUpHref: string;
  locale: string;
  isPending?: boolean;
  heroCta?: ILink[];
  heroFormattedCtas?: FormattedCTA[];
  heroMicrocopy?: string;
  similarCollectionsTitle: string;
  similarCollectionsItems: CategoryGridItem[];
  exploreMoreTitle?: string;
  exploreMoreItems?: CategoryGridItem[];
  inlineGeneration?: boolean;
  generationSuggestions?: SuggestionPill[];
  clickableGenerationResultRows?: boolean;
}

function InnerContent({
  breadcrumbs,
  title,
  description,
  heroCover,
  categoryName,
  soundItems,
  searchSuggestions,
  generateCtaHref,
  onCategorySelect,
  onCategorySearch,
  onSearchSubmit,
  generateFooterTitle,
  generateFooterSubtitle,
  signUpPopoverTitle,
  signUpPopoverCta,
  signUpPopoverFeatures,
  signUpHref,
  locale,
  isPending = false,
  heroCta,
  heroFormattedCtas,
  heroMicrocopy,
  similarCollectionsTitle,
  similarCollectionsItems,
  exploreMoreTitle,
  exploreMoreItems,
  inlineGeneration,
  generationSuggestions,
  clickableGenerationResultRows,
}: MusicCategoryPageClientV2Props) {
  const t = useTranslations("ClientKeys.MusicCategories.inlineGenerator");
  const { playState, activeId, stopActive } = useAudioPlayer();

  const inlineGenerationRef = useRef<HTMLDivElement>(null);
  const scrollFocusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollAnnouncementTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [activeItem, setActiveItem] = useState<SoundEffectExploreItem | null>(null);
  const [generationExpanded, setGenerationExpanded] = useState(false);
  const [generationPrompt, setGenerationPrompt] = useState("");
  const [widgetBumpKey, setWidgetBumpKey] = useState(0);
  const [widgetFocusKey, setWidgetFocusKey] = useState(0);
  const [isSignUpPopoverOpen, setIsSignUpPopoverOpen] = useState(false);
  const [scrollAnnouncement, setScrollAnnouncement] = useState("");

  const showSignUpPopover = useCallback(
    (trigger: string) => {
      captureEvent(EventName.mw_music_category_signup_popover_shown, { trigger });
      setIsSignUpPopoverOpen(true);
    },
    [setIsSignUpPopoverOpen],
  );

  useEffect(() => {
    return () => {
      if (scrollFocusTimerRef.current !== null) clearTimeout(scrollFocusTimerRef.current);
      if (scrollAnnouncementTimerRef.current !== null) clearTimeout(scrollAnnouncementTimerRef.current);
    };
  }, []);

  const scheduleFocus = () => {
    if (scrollFocusTimerRef.current !== null) clearTimeout(scrollFocusTimerRef.current);
    scrollFocusTimerRef.current = setTimeout(() => {
      scrollFocusTimerRef.current = null;
      setWidgetFocusKey(k => k + 1);
    }, 500);
  };

  const announceScroll = () => {
    if (scrollAnnouncementTimerRef.current !== null) clearTimeout(scrollAnnouncementTimerRef.current);
    setScrollAnnouncement("");
    scrollAnnouncementTimerRef.current = setTimeout(() => {
      scrollAnnouncementTimerRef.current = null;
      setScrollAnnouncement(t("scrollAnnouncement"));
    }, 0);
  };

  const { handleDownload } = useMusicDownload({ soundItems, showSignUpPopover });
  const { generateItems, handleGenerateNew } = useMusicGeneration({ showSignUpPopover });

  useEffect(() => {
    if (playState === "idle" && activeId === null && activeItem !== null) {
      setActiveItem(null);
    }
  }, [playState, activeId, activeItem]);

  const handlePlay = (item: SoundEffectExploreItem) => {
    setActiveItem(item);
    setGenerationExpanded(false);
    const position = soundItems.findIndex(s => s.id === item.id);
    captureEvent(EventName.mw_music_category_samples, {
      audio_list_action: "play",
      audio_list_element_id: item.id,
      audio_list_element_position: String(position + 1),
    });
  };

  const handleGenerateSfx = () => {
    captureEvent(EventName.mw_music_category_generate_cta_clicked);
    stopActive();
    setActiveItem(null);
    if (inlineGeneration) {
      inlineGenerationRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      announceScroll();
      scheduleFocus();
    } else {
      setGenerationExpanded(true);
      setWidgetBumpKey(k => k + 1);
    }
  };

  return (
    <>
      <div role='status' aria-live='polite' aria-atomic='true' className='tw-sr-only'>
        {scrollAnnouncement}
      </div>
      <div
        className={clsx(
          "tw-isolate tw-transition-opacity tw-duration-300",
          isPending && "tw-opacity-40 tw-pointer-events-none",
        )}
      >
        <MusicSearchSection
          searchSuggestions={searchSuggestions}
          locale={locale}
          onCategorySelect={onCategorySelect}
          onCategorySearch={onCategorySearch}
          onSearchSubmit={onSearchSubmit}
        />

        <PageSection
          withGap={false}
          withSideLines
          withFilledLine
          topPadding='small'
          bottomPadding='none'
          horizontalPadding='default'
        >
          <HeroSoundEffectsCategoryExplore
            breadcrumbs={breadcrumbs}
            title={title}
            description={description}
            image={heroCover ?? undefined}
            className='tw-mt-0 lg:tw-mt-0'
            eventName={EventName.mw_music_category_hero_cta_clicked}
            cta={heroCta}
            formattedCtas={heroFormattedCtas}
            microcopy={heroMicrocopy}
            compactMobile={inlineGeneration}
          />
        </PageSection>

        <PageSection
          withGap={false}
          withSideLines
          withEdgeLines={true}
          topPadding='small'
          bottomPadding='small'
          horizontalPadding='default'
        >
          <MusicExploreList
            items={soundItems}
            categoryName={categoryName}
            totalCount={soundItems.length}
            generateCtaHref={generateCtaHref}
            generateCtaPosition={3}
            generateCtaShowOnMobile={true}
            generateFooterTitle={inlineGeneration ? undefined : generateFooterTitle}
            generateFooterSubtitle={inlineGeneration ? undefined : generateFooterSubtitle}
            onDownload={handleDownload}
            onPlay={handlePlay}
            onGenerateSfx={handleGenerateSfx}
          />
        </PageSection>

        {inlineGeneration && (
          <PageSection
            withGap={false}
            withSideLines
            withEdgeLines={true}
            withFilledLine
            topPadding='small'
            bottomPadding='small'
            horizontalPadding='default'
          >
            <div ref={inlineGenerationRef} className='tw-flex tw-flex-col tw-gap-6 tw-py-6 lg:tw-py-10 lg:tw-px-20'>
              {(generateFooterTitle || generateFooterSubtitle) && (
                <div className='tw-flex tw-flex-col tw-gap-2 tw-text-center'>
                  {generateFooterTitle && <p className='tw-type-xl tw-text-black'>{generateFooterTitle}</p>}
                  {generateFooterSubtitle && <p className='tw-type-sm tw-text-gray-500'>{generateFooterSubtitle}</p>}
                </div>
              )}
              <MusicGenerationWidget
                isExpanded={true}
                initialPrompt={generationPrompt}
                generateItems={generateItems}
                onDownload={item => handleDownload(item)}
                onPlay={(_item, index) => {
                  setActiveItem(null);
                  captureEvent(EventName.mw_music_category_generation_played, {
                    generationIndex: String(index),
                    source: "programmatic",
                  });
                }}
                onGenerateNew={handleGenerateNew}
                signUpHref={signUpHref}
                fullWidth
                textareaRows={6}
                focusKey={widgetFocusKey}
                suggestions={generationSuggestions}
                clickableResultRows={clickableGenerationResultRows}
              />
            </div>
          </PageSection>
        )}

        <MusicSimilarCollections title={similarCollectionsTitle} items={similarCollectionsItems} />
        {exploreMoreTitle && exploreMoreItems && exploreMoreItems.length > 0 && (
          <MusicSimilarCollections title={exploreMoreTitle} items={exploreMoreItems} />
        )}
      </div>

      <div className={clsx(isSignUpPopoverOpen && "tw-hidden")}>
        <MusicStickyBottomWidget
          activeItem={activeItem}
          generationExpanded={generationExpanded}
          onExpandChange={setGenerationExpanded}
          generationPrompt={generationPrompt}
          generateItems={generateItems}
          onDownload={handleDownload}
          onGenerateNew={handleGenerateNew}
          signUpHref={signUpHref}
          bumpKey={widgetBumpKey}
          hideGenerationWidget={inlineGeneration}
          generationSuggestions={generationSuggestions}
          clickableGenerationResultRows={clickableGenerationResultRows}
        />
      </div>

      <MusicSignUpPopoverSection
        isOpen={isSignUpPopoverOpen}
        onOpenChange={value => {
          setIsSignUpPopoverOpen(value);
          captureEvent(EventName.mw_music_category_signup_popover_closed);
        }}
        title={signUpPopoverTitle}
        ctaText={signUpPopoverCta}
        ctaHref={signUpHref}
        features={signUpPopoverFeatures}
        ctaEventName={EventName.mw_music_category_signup_popover_cta_clicked}
        categoryName={categoryName}
      />
    </>
  );
}

function MusicCategoryPageClientV2(props: MusicCategoryPageClientV2Props) {
  return (
    <AudioListProvider>
      <AudioPlayerProvider>
        <InnerContent {...props} />
      </AudioPlayerProvider>
    </AudioListProvider>
  );
}

MusicCategoryPageClientV2.displayName = "MusicCategoryPageClientV2";

export { MusicCategoryPageClientV2 };
export type { MusicCategoryPageClientV2Props };
